/**
 * AdminView — port of designv2/src/views_admin.jsx, rewired against the live
 * /api/job-roles catalogue and /api/calendar/holidays endpoints (instead of
 * localStorage).
 */
import { useEffect, useState } from "react";

import { DatePicker } from "../cells";
import {
  type AppSettingEntry,
  type SyncJobRow,
  listSettings,
  listSyncJobs,
  patchSetting,
  patchSyncJob,
  runSyncJobNow,
} from "../lib/admin-system-api";
import { apiFetch } from "../lib/api";
import { formatTallinnDateTime } from "../lib/format-time";
import {
  type Holiday,
  invalidateYear,
  setYearHolidays,
  useYearHolidays,
} from "../lib/holiday-cache";
import { createJobRole, deleteJobRole, listJobRoles, renameJobRole } from "../lib/job-roles-api";
import {
  type PartnerCompany,
  type PartnerCompanyApiError,
  createPartnerCompany,
  deletePartnerCompany,
  listPartnerCompanies,
  updatePartnerCompany,
} from "../lib/partner-companies-api";
import { useC } from "../theme";
import { Btn, Card, ConfirmDialog, Field, Input } from "../ui";

export interface AdminViewProps {
  role: string;
}

export function AdminView({ role }: AdminViewProps) {
  const { C } = useC();
  const [tab, setTab] = useState<"roles" | "calendar" | "companies" | "system">("roles");

  if (role !== "admin") {
    return (
      <div style={{ padding: "60px 26px", textAlign: "center", color: C.slateMut }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: C.navyDeep,
            marginBottom: 4,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          Admin only
        </div>
        <div style={{ fontSize: 12 }}>
          Sign in as an admin to access organisation-wide settings.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: C.navyDeep,
            margin: 0,
            fontFamily: "'Poppins',system-ui,sans-serif",
            letterSpacing: -0.3,
          }}
        >
          Admin
        </h2>
        <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
          Organisation-wide settings — role catalogue, calendar, holidays.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: 2,
          marginBottom: 14,
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        {(
          [
            ["roles", "Role Catalogue"],
            ["calendar", "Calendar & Holidays"],
            ["companies", "Companies"],
            ["system", "System"],
          ] as const
        ).map(([v, l]) => (
          <button
            type="button"
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setTab(v)}
            style={{
              padding: "8px 14px",
              border: "none",
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: tab === v ? 600 : 500,
              background: "transparent",
              color: tab === v ? C.blue : C.slate,
              borderBottom: `2px solid ${tab === v ? C.blue : "transparent"}`,
              marginBottom: -1,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "roles" && <RoleCatalogueEditor />}
      {tab === "calendar" && <HolidayEditor />}
      {tab === "companies" && <CompaniesEditor />}
      {tab === "system" && <SystemEditor />}
    </div>
  );
}

function SystemEditor() {
  const { C } = useC();
  const [settings, setSettings] = useState<AppSettingEntry[] | null>(null);
  const [jobs, setJobs] = useState<SyncJobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      Promise.all([listSettings(), listSyncJobs()])
        .then(([s, j]) => {
          if (cancelled) return;
          setSettings(s);
          setJobs(j);
        })
        .catch((err) => {
          if (!cancelled) setError((err as Error).message);
        });
    };
    tick();
    // Poll the jobs list while the System tab is open so the user sees the
    // async tick completion after a Run Now (which leaves nextRunAt=0 until
    // the scheduler picks up the job ~60s later).
    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  async function refresh() {
    try {
      const [s, j] = await Promise.all([listSettings(), listSyncJobs()]);
      setSettings(s);
      setJobs(j);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleRunNow(name: string) {
    setError(null);
    try {
      await runSyncJobNow(name);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleToggleEnabled(name: string, enabled: boolean) {
    try {
      await patchSyncJob(name, { enabled });
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleCadenceChange(name: string, raw: string) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 60) {
      setError("cadence must be an integer >= 60");
      return;
    }
    try {
      await patchSyncJob(name, { cadenceSec: n });
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleSettingSave(key: string, value: string | null) {
    try {
      await patchSetting(key, value);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && (
        <div
          role="alert"
          style={{
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 11.5,
            color: C.r,
          }}
        >
          {error}
        </div>
      )}
      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: C.navyDeep,
            marginBottom: 10,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          Scheduled jobs
        </div>
        <div style={{ fontSize: 11, color: C.slateMut, marginBottom: 10, lineHeight: 1.5 }}>
          The <strong>jira-tempo</strong> job writes Tempo worklog totals into <em>actuals</em> for
          the rolling 2-month window. It is non-destructive: cells Tempo reports get overwritten,
          cells Tempo doesn't cover are left alone. A worklog deleted in Tempo leaves the previously
          synced cell in place — admins can clear it manually, or use the per-project{" "}
          <em>Sync from Jira → Sync ALL</em> button (Projects view) to pull from up to 5 years back.
          No sync ever deletes data.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr>
              {["Name", "Cadence (s)", "Enabled", "Last run", "Status", "Next run", "Run now"].map(
                (h) => (
                  <th
                    key={h}
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      color: C.slateMut,
                      fontSize: 10.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {jobs === null ? (
              <tr>
                <td colSpan={8} style={{ padding: 12, color: C.slateMut, fontStyle: "italic" }}>
                  Loading…
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 12, color: C.slateMut, fontStyle: "italic" }}>
                  No scheduled jobs registered.
                </td>
              </tr>
            ) : (
              jobs.map((j) => (
                <tr key={j.name} style={{ borderTop: `1px solid ${C.lineSoft}` }}>
                  <td style={{ padding: "6px 10px", color: C.navy, fontWeight: 500 }}>{j.name}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <input
                      type="number"
                      defaultValue={j.cadenceSec}
                      onBlur={(e) => void handleCadenceChange(j.name, e.currentTarget.value)}
                      min={60}
                      style={{
                        width: 80,
                        padding: "3px 6px",
                        border: `1px solid ${C.line}`,
                        borderRadius: 4,
                        fontSize: 11.5,
                      }}
                      aria-label={`cadence-${j.name}`}
                    />
                  </td>
                  <td style={{ padding: "6px 10px" }}>
                    <input
                      type="checkbox"
                      checked={j.enabled}
                      onChange={(e) => void handleToggleEnabled(j.name, e.currentTarget.checked)}
                      aria-label={`enabled-${j.name}`}
                    />
                  </td>
                  <td style={{ padding: "6px 10px", color: C.slate }}>
                    {formatTallinnDateTime(j.lastRunAt)}
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      color: j.lastStatus === "error" ? C.r : C.slate,
                      fontWeight: j.lastStatus === "error" ? 600 : 500,
                    }}
                    title={j.lastError ?? j.lastSummary ?? undefined}
                  >
                    {j.lastStatus ?? (j.inFlight ? "in-flight" : "—")}
                    {j.lastError && (
                      <span style={{ fontSize: 10, color: C.slateMut, marginLeft: 4 }}>
                        error: {j.lastError.slice(0, 40)}
                        {j.lastError.length > 40 ? "…" : ""}
                      </span>
                    )}
                    {!j.lastError && j.lastSummary && (
                      <span style={{ fontSize: 10, color: C.slateMut, marginLeft: 4 }}>
                        summary: {j.lastSummary.slice(0, 40)}
                        {j.lastSummary.length > 40 ? "…" : ""}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "6px 10px", color: C.slate }}>
                    {j.inFlight
                      ? "running…"
                      : j.nextRunAt > 0
                        ? formatTallinnDateTime(j.nextRunAt)
                        : "queued"}
                  </td>
                  <td style={{ padding: "6px 10px" }}>
                    <Btn small onClick={() => void handleRunNow(j.name)}>
                      Run now
                    </Btn>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: C.navyDeep,
            marginBottom: 10,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          Runtime settings
        </div>
        {settings === null ? (
          <span style={{ fontSize: 11.5, color: C.slateMut, fontStyle: "italic" }}>Loading…</span>
        ) : settings.length === 0 ? (
          <span style={{ fontSize: 11.5, color: C.slateMut, fontStyle: "italic" }}>
            No runtime settings declared.
          </span>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {settings.map((s) => (
              <SettingRow key={s.key} entry={s} onSave={handleSettingSave} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function SettingRow({
  entry,
  onSave,
}: {
  entry: AppSettingEntry;
  onSave: (key: string, value: string | null) => void | Promise<void>;
}) {
  const { C } = useC();
  const isMasked = entry.secret && entry.set;
  const [draft, setDraft] = useState(isMasked ? "" : (entry.value ?? ""));
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setDraft(entry.secret && entry.set ? "" : (entry.value ?? ""));
    setTouched(false);
  }, [entry.value, entry.secret, entry.set]);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr auto",
        gap: 8,
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 500, color: C.navy }}>
        {entry.key}
        {entry.secret && (
          <span style={{ fontSize: 10, color: C.slateMut, marginLeft: 6 }}>(secret)</span>
        )}
      </div>
      <Input
        value={draft}
        onChange={(v) => {
          setDraft(v);
          setTouched(true);
        }}
        placeholder={isMasked ? "●●●● (enter a new value to replace)" : entry.type}
      />
      <div style={{ display: "flex", gap: 4 }}>
        <Btn
          small
          onClick={() => void onSave(entry.key, draft === "" ? null : draft)}
          disabled={isMasked && !touched}
        >
          Save
        </Btn>
        {entry.set && (
          <Btn
            small
            ghost
            onClick={() => {
              setDraft("");
              void onSave(entry.key, null);
            }}
          >
            Clear
          </Btn>
        )}
      </div>
    </div>
  );
}

function CompaniesEditor() {
  const { C } = useC();
  const [list, setList] = useState<PartnerCompany[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PartnerCompany | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PartnerCompany | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPartnerCompanies()
      .then((rows) => {
        if (!cancelled) setList(rows);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function refresh() {
    try {
      const rows = await listPartnerCompanies();
      setList(rows);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function requestDelete(row: PartnerCompany) {
    setError(null);
    setConfirmDelete(row);
  }

  async function performDelete(row: PartnerCompany) {
    try {
      await deletePartnerCompany(row.id);
      setList((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
    } catch (err) {
      const e = err as PartnerCompanyApiError;
      if (e.status === 409) {
        setError(`Cannot delete "${row.name}" — it is referenced by at least one person.`);
      } else {
        setError(e.message);
      }
    }
  }

  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: C.navyDeep,
              fontFamily: "'Poppins',system-ui,sans-serif",
            }}
          >
            Partner companies
          </div>
          <div style={{ fontSize: 11, color: C.slateMut, marginTop: 2, lineHeight: 1.45 }}>
            Registry of external supplier / subcontractor entities. Linked from people whose
            engagement is set to Partner.
          </div>
        </div>
        <Btn primary small onClick={() => setCreating(true)}>
          + Add company
        </Btn>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            borderRadius: 6,
            padding: "6px 10px",
            marginBottom: 10,
            fontSize: 11.5,
            color: C.r,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ border: `1px solid ${C.lineSoft}`, borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr style={{ background: "transparent" }}>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  color: C.slateMut,
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  color: C.slateMut,
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                EE registry
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  color: C.slateMut,
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                EU VAT id
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  color: C.slateMut,
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Billing email
              </th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {list === null ? (
              <tr>
                <td colSpan={5} style={{ padding: 12, color: C.slateMut, fontStyle: "italic" }}>
                  Loading…
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ padding: 12, color: C.slateMut, fontStyle: "italic" }}
                  data-testid="companies-empty"
                >
                  No companies yet.
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row.id} style={{ borderTop: `1px solid ${C.lineSoft}` }}>
                  <td style={{ padding: "6px 10px", color: C.navy, fontWeight: 500 }}>
                    <button
                      type="button"
                      data-testid={`edit-company-${row.id}`}
                      onClick={() => setEditing(row)}
                      style={{
                        background: "transparent",
                        border: 0,
                        color: "inherit",
                        font: "inherit",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {row.name}
                    </button>
                  </td>
                  <td style={{ padding: "6px 10px", color: C.slate }}>
                    {row.eeRegistryCode ?? "—"}
                  </td>
                  <td style={{ padding: "6px 10px", color: C.slate }}>{row.euVatId ?? "—"}</td>
                  <td style={{ padding: "6px 10px", color: C.slate }}>{row.billingEmail ?? "—"}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>
                    <button
                      type="button"
                      data-testid={`delete-company-${row.id}`}
                      onClick={() => requestDelete(row)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: C.r,
                        fontSize: 14,
                        padding: 2,
                        lineHeight: 1,
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <CompanyModal
          mode="create"
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={async (created) => {
            setList((prev) => (prev ? [created, ...prev] : [created]));
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <CompanyModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async (updated) => {
            setList((prev) => (prev ? prev.map((r) => (r.id === updated.id ? updated : r)) : prev));
            setEditing(null);
            await refresh();
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete partner company"
          message={`Delete partner company "${confirmDelete.name}"?`}
          destructive
          confirmLabel="Delete"
          onConfirm={() => performDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </Card>
  );
}

function CompanyModal({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  initial: PartnerCompany | null;
  onClose: () => void;
  onSaved: (row: PartnerCompany) => void | Promise<void>;
}) {
  const { C } = useC();
  const [name, setName] = useState(initial?.name ?? "");
  const [eeReg, setEeReg] = useState(initial?.eeRegistryCode ?? "");
  const [euVat, setEuVat] = useState(initial?.euVatId ?? "");
  const [billing, setBilling] = useState(initial?.billingEmail ?? "");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setFieldError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldError("Name is required.");
      return;
    }
    setBusy(true);
    try {
      const input = {
        name: trimmed,
        eeRegistryCode: eeReg.trim() ? eeReg.trim() : null,
        euVatId: euVat.trim() ? euVat.trim() : null,
        billingEmail: billing.trim() ? billing.trim() : null,
      };
      let saved: PartnerCompany;
      if (mode === "create" || !initial) {
        saved = await createPartnerCompany(input);
      } else {
        saved = await updatePartnerCompany(initial.id, input);
      }
      await onSaved(saved);
    } catch (err) {
      const e = err as PartnerCompanyApiError;
      setFieldError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative showModal() — this codebase uses controlled overlays
      role="dialog"
      aria-modal="true"
    >
      <Card style={{ padding: 18, width: 380 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.navyDeep,
            marginBottom: 12,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          {mode === "create" ? "Add company" : `Edit ${initial?.name}`}
        </div>
        {fieldError && (
          <div
            role="alert"
            style={{
              background: C.rBg,
              border: `1px solid ${C.rBd}`,
              borderRadius: 6,
              padding: "6px 10px",
              marginBottom: 10,
              fontSize: 11.5,
              color: C.r,
            }}
            data-testid="company-modal-error"
          >
            {fieldError}
          </div>
        )}
        <Field label="Name">
          <Input value={name} onChange={setName} placeholder="Foo OÜ" />
        </Field>
        <Field label="EE business registry code">
          <Input value={eeReg} onChange={setEeReg} placeholder="12345678" />
        </Field>
        <Field label="EU VAT id">
          <Input value={euVat} onChange={setEuVat} placeholder="EE123456789" />
        </Field>
        <Field label="Billing email">
          <Input value={billing} onChange={setBilling} placeholder="billing@partner.example" />
        </Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

/* Holiday editor — per-year list. Read-only previews the fixed Estonian
   defaults plus floating Easter-derived ones, both computed server-side.
   Admin-added extras CRUD via /api/calendar/holidays/{year}. The list is
   read directly from the per-year holiday cache so the same data drives
   the grid's hours↔percent conversion. */
function HolidayEditor() {
  const { C } = useC();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const list = useYearHolidays(year);
  const [name, setName] = useState("");
  const [date, setDate] = useState(`${year}-01-01`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function describeError(res: Response, fallback: string): Promise<string> {
    try {
      const body = (await res.clone().json()) as {
        message?: string;
        fields?: string[];
        code?: string;
      };
      if (body.message) {
        return body.fields?.length ? `${body.message} (${body.fields.join(", ")})` : body.message;
      }
    } catch {
      // body wasn't JSON; fall through
    }
    return `${fallback} (${res.status})`;
  }

  /** Run a CRUD action against /api/calendar/holidays for `targetYear`
   *  and reload that year on success so the cache (and therefore the
   *  rendered table) reflects the new state. Returns whether the action
   *  succeeded so callers can clear an input only when the write took. */
  async function runMutation(
    targetYear: number,
    action: () => Promise<Response>,
    errorPrefix: string,
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const res = await action();
      if (!res.ok && res.status !== 204) {
        setError(await describeError(res, errorPrefix));
        return false;
      }
      try {
        const reload = await apiFetch(`/api/calendar/holidays/${targetYear}`);
        if (!reload.ok) {
          setError(await describeError(reload, "Failed to refresh holidays"));
          return false;
        }
        const body: unknown = await reload.json();
        if (!Array.isArray(body)) {
          setError("Failed to refresh holidays (unexpected shape)");
          return false;
        }
        setYearHolidays(targetYear, body as Holiday[]);
      } catch (err) {
        setError((err as Error).message || "Failed to refresh holidays");
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }

  async function addExtra() {
    const trimmed = name.trim();
    if (!trimmed || !date) return;
    const [yStr, moStr, ddStr] = date.split("-");
    const y = Number(yStr);
    const mo = Number(moStr);
    const dd = Number(ddStr);
    if (!Number.isInteger(y) || !Number.isInteger(mo) || !Number.isInteger(dd)) return;
    if (y !== year) setYear(y);
    const ok = await runMutation(
      y,
      () =>
        apiFetch(`/api/calendar/holidays/${y}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ month: mo, day: dd, name: trimmed }),
        }),
      "Could not add holiday",
    );
    if (ok) setName("");
  }

  async function removeExtra(id: string) {
    await runMutation(
      year,
      () => apiFetch(`/api/calendar/holidays/${year}/${id}`, { method: "DELETE" }),
      "Could not remove holiday",
    );
  }

  // Older callers may have invalidated the cache before the editor mounted;
  // ensure the year currently in view is fresh. Idempotent — `invalidateYear`
  // queues a fetch only if no in-flight request exists for the year.
  useEffect(() => {
    invalidateYear(year);
  }, [year]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Card style={{ padding: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: C.navyDeep,
              fontFamily: "'Poppins',system-ui,sans-serif",
            }}
          >
            Holidays · {year}
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {[year - 1, year, year + 1].map((y) => (
              <button
                type="button"
                key={y}
                onClick={() => setYear(y)}
                style={{
                  padding: "3px 9px",
                  borderRadius: 5,
                  border: `1px solid ${y === year ? C.blue : C.line}`,
                  background: y === year ? C.blueTint1 : "transparent",
                  color: y === year ? C.blueDeep : C.slate,
                  fontSize: 11,
                  fontWeight: y === year ? 600 : 500,
                  cursor: "pointer",
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                }}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: C.slateMut, marginBottom: 10, lineHeight: 1.45 }}>
          Fixed + floating (Easter-based) Estonian public holidays are built in. Business-hour
          calculations subtract them automatically. Add extras below.
        </div>
        {error && (
          <div style={{ color: C.r, fontSize: 11, marginBottom: 8 }} role="alert">
            {error}
          </div>
        )}
        <div
          style={{
            maxHeight: 340,
            overflowY: "auto",
            border: `1px solid ${C.lineSoft}`,
            borderRadius: 6,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <tbody>
              {list.map((h, i) => (
                <tr
                  key={`${h.kind}-${h.id ?? `${h.month}-${h.day}-${i}`}`}
                  style={{ borderBottom: `1px solid ${C.lineSoft}` }}
                >
                  <td
                    style={{
                      padding: "6px 10px",
                      color: C.slateMut,
                      fontVariantNumeric: "tabular-nums",
                      width: 70,
                    }}
                  >
                    {String(h.month).padStart(2, "0")}-{String(h.day).padStart(2, "0")}
                  </td>
                  <td style={{ padding: "6px 10px", color: C.navy, fontWeight: 500 }}>{h.name}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>
                    {h.kind === "extra" && h.id ? (
                      <button
                        type="button"
                        onClick={() => void removeExtra(h.id ?? "")}
                        disabled={busy}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: busy ? "wait" : "pointer",
                          color: C.r,
                          fontSize: 14,
                          padding: 2,
                          lineHeight: 1,
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    ) : (
                      <span
                        style={{
                          fontSize: 9.5,
                          color: C.slateDim,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {h.kind === "fixed" ? "fixed" : "floating"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: C.navyDeep,
            marginBottom: 10,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          Add a custom holiday
        </div>
        <Field label="Date">
          <DatePicker value={date || `${year}-01-01`} onChange={setDate} label="" />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={setName} placeholder="E.g. Company day" />
        </Field>
        <Btn
          primary
          onClick={() => void addExtra()}
          disabled={busy || !name.trim()}
          style={{ marginTop: 4 }}
        >
          {busy ? "Adding…" : "+ Add Holiday"}
        </Btn>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${C.lineSoft}`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: C.slateMut,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              marginBottom: 6,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            Week starts
          </div>
          <div style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.5 }}>
            Monday (ISO 8601) — used across all calendars and the 12-month forward focus window.
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${C.lineSoft}`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: C.slateMut,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              marginBottom: 6,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            Business hours
          </div>
          <div style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.5 }}>
            8 h × weekday × (days − holidays). Working-month hours feed allocation → hours
            conversion across every view.
          </div>
        </div>
      </Card>
    </div>
  );
}

function RoleCatalogueEditor() {
  const { C } = useC();
  const [list, setList] = useState<string[] | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listJobRoles()
      .then((rows) => {
        if (!cancelled) setList(rows);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function add() {
    const v = draft.trim();
    if (!v || (list ?? []).some((x) => x.toLowerCase() === v.toLowerCase())) return;
    try {
      await createJobRole(v);
      setList((prev) => (prev ? [...prev, v].sort() : [v]));
      setDraft("");
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }
  async function remove(name: string) {
    try {
      await deleteJobRole(name);
      setList((prev) => (prev ? prev.filter((x) => x !== name) : prev));
    } catch (err) {
      setError((err as Error).message);
    }
  }
  async function rename(oldName: string, next: string) {
    const n = next.trim();
    if (!n || (list ?? []).some((x) => x.toLowerCase() === n.toLowerCase())) return;
    try {
      await renameJobRole(oldName, n);
      setList((prev) => (prev ? prev.map((x) => (x === oldName ? n : x)).sort() : prev));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: C.navyDeep,
          marginBottom: 4,
          fontFamily: "'Poppins',system-ui,sans-serif",
        }}
      >
        Job roles
      </div>
      <div style={{ fontSize: 11, color: C.slateMut, marginBottom: 12, lineHeight: 1.45 }}>
        These appear in the Members role picker and the project rate card. They're suggestions —
        anyone can still type a custom role on the fly.
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            borderRadius: 6,
            padding: "6px 10px",
            marginBottom: 10,
            fontSize: 11.5,
            color: C.r,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {list === null ? (
          <span style={{ fontSize: 11.5, color: C.slateMut, fontStyle: "italic" }}>Loading…</span>
        ) : list.length === 0 ? (
          <span style={{ fontSize: 11.5, color: C.slateMut, fontStyle: "italic" }}>
            No roles yet.
          </span>
        ) : (
          list.map((r) => (
            <RolePill key={r} name={r} onRename={(n) => rename(r, n)} onRemove={() => remove(r)} />
          ))
        )}
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          aria-label="New role name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void add();
          }}
          placeholder="New role name…"
          style={{
            flex: 1,
            padding: "7px 11px",
            borderRadius: 6,
            border: `1px solid ${C.line}`,
            fontSize: 12,
            outline: "none",
            color: C.navy,
            background: C.surface,
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        />
        <Btn primary small onClick={() => void add()} disabled={!draft.trim()}>
          + Add
        </Btn>
      </div>
    </Card>
  );
}

function RolePill({
  name,
  onRename,
  onRemove,
}: {
  name: string;
  onRename: (next: string) => void;
  onRemove: () => void;
}) {
  const { C } = useC();
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(name);
  if (!edit) {
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: C.blueTint1,
          border: `1px solid ${C.blueTint4}`,
          borderRadius: 14,
          padding: "3px 4px 3px 10px",
          fontSize: 11.5,
          color: C.blueDeep,
          fontWeight: 500,
        }}
      >
        <button
          type="button"
          onClick={() => setEdit(true)}
          title="Click to rename"
          style={{
            cursor: "pointer",
            background: "transparent",
            border: 0,
            color: "inherit",
            font: "inherit",
            padding: 0,
          }}
        >
          {name}
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          title="Remove"
          style={{
            width: 18,
            height: 18,
            borderRadius: 9,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: C.slateMut,
            fontSize: 13,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    );
  }
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: C.surface,
        border: `1px solid ${C.blue}`,
        borderRadius: 14,
        padding: "1px 4px 1px 6px",
      }}
    >
      <input
        aria-label={`Rename ${name}`}
        ref={(el) => el?.focus()}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => {
          if (val.trim() && val !== name) onRename(val);
          setEdit(false);
          setVal(name);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (val.trim() && val !== name) onRename(val);
            setEdit(false);
          }
          if (e.key === "Escape") {
            setEdit(false);
            setVal(name);
          }
        }}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: 11.5,
          color: C.blueDeep,
          fontWeight: 500,
          width: Math.max(60, val.length * 7 + 10),
          padding: "3px 2px",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      />
    </div>
  );
}
